import { DEFAULT_APPEARANCE } from '@/theme/engine.mjs';
import { variantSummary } from "./variant-quantities.mjs";
import { rupiah } from "@/lib/format";

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

const methodLabel = { tunai: "Tunai", transfer: "Transfer", qris: "QRIS" };

// Browser text weight is separate from Epson hardware heat/density settings.
export function buildReceiptHtml(tx, s = {}, cashier = "") {
  if (!tx) return '';
  const printWidth = String(s.paper) === '58' ? 48 : 72;
  const date = new Date(tx.created_at);
  const dateStr = date.toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const line = '<div class="hr"></div>';
  const row = (l, r, b) => `<div class="row${b ? " b" : ""}"><span>${escapeHtml(l)}</span><span>${escapeHtml(r)}</span></div>`;

  let items = "";
  tx.items.forEach((it) => {
    const lvl = (it.price_level || "").slice(0, 3).toUpperCase();
    items += `<div class="it"><div class="item-name">${escapeHtml(it.name)}</div>` +
      row(`${it.qty} x ${rupiah(it.price)}${lvl ? ` (${lvl})` : ""}`, rupiah(it.qty * it.price));
    if (variantSummary(it)) items += `<div class="it-variants">${escapeHtml(variantSummary(it))}</div>`;
    if (it.discount > 0) items += `<div class="row"><span>&nbsp;&nbsp;Diskon item</span><span>-${rupiah(it.discount)}</span></div>`;
    if (it.note) items += `<div class="it-note">* ${escapeHtml(it.note)}</div>`;
    items += `</div>`;
  });

  let pays = "";
  (tx.checkout_payments || tx.payments || []).forEach((p) => { pays += row(methodLabel[p.method] || p.method, rupiah(p.amount)); });

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Struk ${escapeHtml(tx.invoice_no)}</title>
    <style>
      /* Driver chooses roll length. "80mm auto" is not a valid CSS page size. */
      @page { margin: 0; size: auto; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      html, body { margin: 0; padding: 0; background: #fff; color: #000; }
      /* Includes padding: never send a 78mm-wide document to a 72mm print head. */
      /* Keep a safe inset on both sides; do not shift glyphs into the hardware margin. */
      #r { width: ${printWidth}mm; max-width: 100%; margin: 0; padding: 2mm 2mm 2mm 4mm; font-family: Arial, Helvetica, sans-serif; font-size: 13px; font-weight: 400; line-height: 1.35; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
      #r, #r * { color: #000 !important; -webkit-text-fill-color: #000; opacity: 1; text-shadow: none; }
      .c { text-align: center; }
      .big, .total, .b { font-family: Arial, Helvetica, sans-serif; }
      .b { font-weight: 700; }
      .big { font-size: 16px; font-weight: 700; }
      .sm { font-size: 13px; }
      .hr { border-top: 1px dashed #000; margin: 5px 0; }
      .row, .total { display: flex; justify-content: space-between; gap: 8px; }
      .row > span, .total > span { min-width: 0; }
      .row > span:last-child:not(:first-child), .total > span:last-child:not(:first-child) { flex-shrink: 0; max-width: 60%; text-align: right; }
      .it { margin-bottom: 3px; }
      .it-note { white-space: pre-wrap; }
      .total { font-weight: 700; font-size: 14px; margin-top: 2px; }
    </style></head><body><div id="r">
      <div class="c big">${escapeHtml(s.store_name || DEFAULT_APPEARANCE.branding.name)}</div>
      ${s.address ? `<div class="c sm">${escapeHtml(s.address)}</div>` : ""}
      ${s.phone ? `<div class="c sm">Telp ${escapeHtml(s.phone)}</div>` : ""}
      ${line}
      <div class="row"><span>No</span><span>${escapeHtml(tx.invoice_no)}</span></div>
      <div class="row"><span>${dateStr}</span></div>
      <div class="row"><span>Kasir: ${escapeHtml(cashier)}</span></div>
      ${line}
      ${items}
      ${line}
      ${row("Jumlah Produk", tx.items.length)}
      ${row("Subtotal", rupiah(tx.subtotal))}
      ${tx.discount > 0 ? row("Diskon", "-" + rupiah(tx.discount)) : ""}
      ${(tx.previous_debts || []).map(debt => row("Piutang sebelumnya (" + debt.invoice_no.slice(-4) + ")", rupiah(debt.amount))).join("")}
      <div class="total"><span>TOTAL</span><span>${rupiah(tx.total + (tx.settlement_total || 0))}</span></div>
      ${tx.fee > 0 ? row("Biaya", rupiah(tx.fee)) + row("TOTAL BAYAR", rupiah(tx.grand_total), true) : ""}
      ${tx.return_credit_applied > 0 ? row("Saldo Retur", "-" + rupiah(tx.return_credit_applied)) : ""}
      ${line}
      ${pays}
      ${row("Bayar", rupiah(tx.paid))}
      ${row("Kembali", rupiah(tx.change))}
      ${tx.customer_name ? row("Pelanggan", tx.customer_name) : ""}
      ${tx.credit > 0 ? row("SISA PIUTANG", rupiah(tx.credit)) : ""}
      ${line}
      ${tx.note ? `<div class="sm" style="white-space:pre-wrap;overflow-wrap:anywhere">Catatan: ${escapeHtml(tx.note)}</div>${line}` : ""}
      <div class="c sm" style="white-space:pre-line">${escapeHtml(s.footer || "*** TERIMA KASIH ***")}</div>
    </div>
    <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},150);};window.onafterprint=function(){window.close();};</script>
    </body></html>`;

  return html;
}

export function printReceipt(tx, s = {}, cashier = "") {
  if (!tx) return false;
  return printDocument(buildReceiptHtml(tx, s, cashier));
}

// Uses the actual receipt template, with synthetic data only. No API or stock writes.
export function buildReceiptFontTestHtml(s = {}) {
  const sample = {
    invoice_no: 'TES-FONT', created_at: '2026-01-01T12:00:00Z',
    items: [{ name: 'KAPAL API SPECIAL MIX GULA', qty: 3, price: 12500, discount: 0 }],
    subtotal: 37500, total: 37500, paid: 50000, change: 12500,
    payments: [{ method: 'tunai', amount: 50000 }],
  };
  const base = buildReceiptHtml(sample, { ...s, footer: 'TES CETAK — BUKAN TRANSAKSI' }, 'Tes Font');
  const start = base.indexOf('<div id="r">');
  const end = base.indexOf('<script>', start);
  const receipt = base.slice(start, end);
  // Keep a single document/print job. Only the detail size differs between samples.
  const samples = [13, 14].map(size => receipt
    .replace('<div id="r">', `<div id="font-test-${size}" class="font-test"><div style="font-size:${size}px;font-weight:400">CONTOH ARIAL NORMAL ${size}px</div>`)).join('');
  return base.slice(0, start).replaceAll('#r', '.font-test')
    + `<style>#font-test-14, #font-test-14 .sm { font-size:14px; } .font-test { break-inside:avoid; }</style>`
    + samples + base.slice(end);
}

export function printReceiptFontTest(s = {}) {
  return printDocument(buildReceiptFontTestHtml(s));
}

function printDocument(html) {
  const w = window.open("", "_blank", "width=360,height=640");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
